<template>
  <div
    class="card-wrap__outer rule-campaigns"
    :class="{
      'rule-campaigns_inactive': rule.status === 'inactive',
      'plan-limits-warning__rules': showPlanLimitsWarning,
    }"
  >
    <ui-tooltip-icon
      class="plan-limits-warning__tooltip"
      preset="error"
      v-if="showPlanLimitsWarning"
      :text="t('This option is no longer active due to the subscription change to the plan which doesn’t include this option or this add-on is switched off in your subscriptions settings.')"
    ></ui-tooltip-icon>
    <h4 class="rule-campaigns__header">
      <div class="rule-campaigns__header-title align-center">
        <div class="rule-campaigns__header-name">
          <ui-input
            v-if="reportingFlowRulePathEnabled"
            class="path-name"
            v-model="rule.name"
            placeholder="Rule Name"
          />
        </div>
        <div class="rule-campaigns__header-conditions condition">
          <span
            v-for="(contition, index) in ruleConditions"
            :key="index"
            class="condition__rule"
            :class="{
              'condition__rule--opposite-rule': !contition.black && isOppositeFlowRulePredicate(contition.predicate),
              'condition__rule--black': contition.black,
            }"
          >{{ contition.name }}</span>
        </div>
        <span v-if="!rule.name && (!ruleConditions || ruleConditions.length === 0)">{{ t('Empty Conditions') }}</span>
        <ui-icon
          v-if="isAtLeastOneActivePathError || isNoConditionError || isAnyRuleHasError"
          size="12"
          class="ml-4"
          :color="$colors.tangerine"
          icon="fas fa-exclamation-triangle"
        ></ui-icon>
      </div>
      <div v-if="ruleIndex !== 0" class="rule-campaigns__header-actions">
        <div class="rule-campaigns__header-action-block">
          <ui-icon
            class="rule-campaigns__edit-button"
            size="14"
            @click="onAddNewRule(rule)"
            icon="fas fa-pencil-alt"
          ></ui-icon>
        </div>

        <div class="rule-campaigns__header-action-block">
          <a
            class="rule-campaigns__header-action-move"
            @click="moveRule(ruleIndex, form.flowInline.rules, moveDirectionEnum.up)"
          >
            <ui-icon
              :color="ruleIndex > 1 ? $colors.green : $colors.gray20"
              size="16"
              icon="fas fa-chevron-up"
            ></ui-icon>
          </a>
          <a
            class="rule-campaigns__header-action-move"
            @click="moveRule(ruleIndex, form.flowInline.rules, moveDirectionEnum.down)"
          >
            <ui-icon
              :color="ruleIndex < form.flowInline.rules.length - 1 ? $colors.green : $colors.gray20"
              size="16"
              icon="fas fa-chevron-down"
            ></ui-icon>
          </a>
        </div>

        <div class="rule-campaigns__header-action-block">
          <ui-icon
            class="rule-campaigns__delete-button"
            size="14"
            @click="deleteRule()"
            icon="fas fa-trash-alt"
          ></ui-icon>
        </div>

        <div class="rule-campaigns__header-action-block rule-campaigns__header-action-block_always-show">
          <ui-switcher
            v-model="rule.status"
            true-value="active"
            false-value="inactive"
          ></ui-switcher>
        </div>
      </div>
    </h4>

    <create-campaign-dialog-flow-path
      v-for="(path, index) in rule.paths"
      :isDisabledWeight="isDisabledWeight"
      :key="index"
      :path="path"
      :country-id="countryId"
      :paths="rule.paths"
      :rule-index="ruleIndex"
      :path-index="index"
      :landings="landings"
      :offerOptions="offerOptions"
      :landingOptions="landingOptions"
      :offers="offers"
      :form="form"
      :errors-raw="errorsRaw"
      :error-condition-prefix="errorConditionPrefix"
      :dialogRepositionParam="dialogRepositionParam"
    ></create-campaign-dialog-flow-path>

    <div class="d-flex justify-content-center rule-campaigns__footer">
      <ui-button
        size="large"
        button-style="plain"
        @click="addNewPath(rule)"
        :text="t('+ Add new path')"
        class="wide-btn"
      ></ui-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useColors } from '@/composables';
import CreateCampaignDialogFlowPath from './create-campaign-dialog-flow-path.vue';
import { useCampaignFlowRule } from './useCampaignFlowRule';

const $colors = useColors();

const props = defineProps<{
  ruleIndex: number;
  isDisabledWeight: boolean;
  rule: Record<string, unknown> & {
    name?: string;
    status?: string;
    conditions?: Record<string, unknown>;
    paths?: Array<Record<string, unknown>>;
    lastPathNumber?: number;
    default?: boolean;
  };
  offerOptions: Offer[];
  landingOptions: Landing[];
  form: Record<string, unknown> & {
    flowInline: { rules: unknown[] };
  };
  countryId: string;
  landings: unknown[];
  offers: unknown[];
  errorConditionPrefix: string[];
  dialogRepositionParam: string;
  errorsRaw: unknown[][] | null;
}>();

const emit = defineEmits<{
  'add-new-rule': [rule: typeof props.rule];
}>();

const {
  t,
  moveDirectionEnum,
  reportingFlowRulePathEnabled,
  showPlanLimitsWarning,
  ruleConditions,
  isOppositeFlowRulePredicate,
  isNoConditionError,
  isAtLeastOneActivePathError,
  isAnyRuleHasError,
  moveRule,
  addNewPath,
  deleteRule,
  onAddNewRule,
} = useCampaignFlowRule(props, (rule) => emit('add-new-rule', rule));
</script>

<style lang="scss" scoped>
.wide-btn {
  width: 100%;
  display: flex;

  :deep(.ui-button) {
    width: 100%;
    justify-content: center;
  }

  :deep(.ui-button__main) {
    justify-content: center;
  }
}

.plan-limits-warning__rules {
  border: 1px solid var(--color-red);
  position: relative;
  .plan-limits-warning__tooltip {
    right: 4px;
  }
}
</style>
